import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Plus, ScanLine, BarChart3, Package, IndianRupee, TrendingUp } from "lucide-react";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/orders").then((r) => setRecent((r.data || []).slice(0, 6))).catch(() => {});
  }, []);

  const Tile = ({ label, value, icon: Icon, color = "#0A0A0A", testid }) => (
    <div className="brut-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="label-mono !mb-0">{label}</div>
        <Icon size={18} strokeWidth={2.5} style={{ color }} />
      </div>
      <div className="font-heading font-black text-4xl tracking-tighter">{value}</div>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#E63946] font-bold mb-2">── COMMAND CENTER</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">DASHBOARD.</h1>
        </div>
        <Link to="/orders/new" className="brut-btn brut-btn-primary" data-testid="cta-new-order">
          <Plus size={18} strokeWidth={3} /> NEW ORDER
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        <Tile testid="stat-today" label="Today's Orders" value={stats?.today_orders ?? "—"} icon={TrendingUp} color="#E63946" />
        <Tile testid="stat-total" label="Total Orders" value={stats?.total_orders ?? "—"} icon={Package} />
        <Tile testid="stat-qty" label="Total Quantity" value={stats?.total_quantity ?? "—"} icon={BarChart3} color="#2A9D8F" />
        <Tile testid="stat-amt" label="Total Value (₹)" value={stats?.total_amount ? Math.round(stats.total_amount).toLocaleString("en-IN") : "—"} icon={IndianRupee} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 brut-card">
          <div className="flex items-center justify-between px-5 py-4 border-b-2 border-black">
            <div className="font-heading font-black text-xl">RECENT ORDERS</div>
            <Link to="/orders" className="text-xs uppercase tracking-wider font-bold underline" data-testid="link-view-all">VIEW ALL →</Link>
          </div>
          <div className="divide-y-2 divide-black/10">
            {recent.length === 0 && (
              <div className="px-5 py-8 text-sm text-[#666] uppercase tracking-wider">No orders yet. Create one →</div>
            )}
            {recent.map((o) => (
              <Link
                key={o.id}
                to={`/orders/${o.id}`}
                data-testid={`order-row-${o.order_no}`}
                className="flex items-center justify-between px-5 py-4 hover:bg-[#F4F4F0]"
              >
                <div>
                  <div className="font-bold text-sm">{o.order_no}</div>
                  <div className="text-xs text-[#666] uppercase tracking-wider">
                    {o.customer.name} · {o.customer.city} · Agent: {o.customer.agent}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-heading font-black text-lg">{o.total_quantity} PCS</div>
                  <div className="text-xs text-[#666]">₹{Math.round(o.total_amount).toLocaleString("en-IN")}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <Link to="/orders/new" className="brut-card p-5 block hover:bg-[#F4F4F0]" data-testid="quick-new">
            <Plus className="mb-3 text-[#E63946]" strokeWidth={3} />
            <div className="font-heading font-black text-xl">CREATE ORDER</div>
            <div className="text-xs uppercase tracking-wider text-[#666] mt-1">3-step wizard</div>
          </Link>
          <Link to="/barcodes" className="brut-card p-5 block hover:bg-[#F4F4F0]" data-testid="quick-barcode">
            <ScanLine className="mb-3" strokeWidth={3} />
            <div className="font-heading font-black text-xl">BARCODES</div>
            <div className="text-xs uppercase tracking-wider text-[#666] mt-1">Generate printable labels</div>
          </Link>
          <Link to="/reports" className="brut-card p-5 block hover:bg-[#F4F4F0]" data-testid="quick-reports">
            <BarChart3 className="mb-3 text-[#2A9D8F]" strokeWidth={3} />
            <div className="font-heading font-black text-xl">REPORTS</div>
            <div className="text-xs uppercase tracking-wider text-[#666] mt-1">SKU & Agent</div>
          </Link>
        </div>
      </div>
    </div>
  );
}
